import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Target, Brain, Zap, Palette, BarChart3, Check, Star, Crown, Sparkles, Info } from "lucide-react";
import Logo from "../components/Logo";
import ROICalculator from "../components/ROICalculator";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613545325278-f24b0cae1224?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwaW50ZXJpb3IlMjBtb2Rlcm4lMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc4MDAxNzIxNnww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Target, title: "Buyer & Seller Lead Engine", desc: "Capture both buyer and seller leads: name, phone, email, budget, and property value, straight from Meta Ads." },
  { icon: Zap, title: "Call & Text From Your CRM", desc: "Get your own local number and call or text any lead right inside Newground. Use browser calling or ring your cell, plus instant new-lead text alerts." },
  { icon: Brain, title: "AI Lead Intelligence", desc: "AI reads each conversation and gives you the lead's intent, the next best action, and a ready-to-send reply you can fire off in one tap." },
  { icon: Palette, title: "Listing Campaign Kits", desc: "Paste a listing link and get a full ad kit with hero, lifestyle, carousel and reel creatives plus ready-to-run copy, then promote it with one click." },
  { icon: BarChart3, title: "Real-Time Analytics", desc: "See leads, conversions, and ROI updated live. Know what works, and double down." },
];

const steps = [
  { num: "01", icon: Target, title: "Launch your Lead Engine", desc: "Set your market and budget. Newground runs your buyer, seller and listing ad campaigns automatically." },
  { num: "02", icon: Brain, title: "Leads land, scored by AI", desc: "Every lead flows into your dashboard with an AI intent score 1–10, so you see who's ready to move." },
  { num: "03", icon: Palette, title: "Promote any listing", desc: "Paste a listing link to generate a full Campaign Kit with hero, lifestyle, carousel and reel, then promote it in one click." },
  { num: "04", icon: Sparkles, title: "Call & close in your CRM", desc: "Call and text leads from your own number while AI Lead Intelligence reads the thread and drafts your replies." },
];

const proIncludes = [
  "Buyer Lead Engine",
  "AI Lead Scoring",
  "AI CRM to call & text leads in-app",
  "AI Lead Intelligence & reply drafting",
  "Creative Maker with Listing Campaign Kits",
  "Monthly calling & texting credits",
  "Real-Time Analytics",
];

const eliteIncludes = [
  "Everything in Pro",
  "Seller Lead Engine",
  "Seller Listing Campaign Kits",
  "Priority Support",
];

const DashboardPreview = () => {
  const kpis = [
    { label: "Leads", value: "42", trend: "+18% vs last month", tone: "slate" },
    { label: "Qualified", value: "15", trend: "36% of leads", tone: "slate" },
    { label: "High Intent", value: "7", trend: "ready to close", tone: "gold" },
    { label: "Pipeline", value: "$68,000", trend: "+5 deals", tone: "blue" },
  ];
  const leads = [
    { name: "Daniel Cho", note: "Buyer · $2.4M–$3M · Bel Air", status: "Hot", tone: "gold" },
    { name: "Marcus Holloway", note: "Buyer · $1.2M–$1.5M · Pacific Heights", status: "Qualified", tone: "green" },
    { name: "Sarah Mitchell", note: "Seller · $3M+ · Lake Washington", status: "New", tone: "blue" },
  ];
  const toneCard = {
    slate: "bg-slate-50 border-slate-200 text-slate-500",
    gold: "bg-amber-50 border-amber-200 text-amber-600",
    blue: "border-[#1D6AFF]/20 text-[#1D6AFF]",
  };
  const pill = {
    gold: "bg-amber-50 text-amber-700 border-amber-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-[#1D6AFF]/10 text-[#1552C6] border-[#1D6AFF]/20",
  };
  return (
    <div className="relative max-w-5xl mx-auto -mt-20 sm:-mt-28 px-4 z-20" data-testid="hero-dashboard-preview">
      <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-7 shadow-[0_40px_100px_-20px_rgba(15,23,42,0.25)] animate-float">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <div className="text-xs text-slate-500 font-mono">app.newgroundai.com/dashboard</div>
          <div className="w-12" />
        </div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-slate-900">This month</div>
          <div className="text-[11px] text-slate-400">Updated live</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {kpis.map((k) => (
            <div key={k.label} className={`rounded-2xl p-4 sm:p-5 border ${k.tone === "blue" ? "bg-[#1D6AFF]/[0.06]" : ""} ${toneCard[k.tone]}`}>
              <div className={`text-xs uppercase tracking-wider mb-2 font-semibold ${k.tone === "slate" ? "text-slate-500" : ""}`}>{k.label}</div>
              <div className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{k.value}</div>
              <div className="text-[11px] text-emerald-600 mt-1 font-medium">{k.trend}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 sm:mt-5 bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-900">Recent Leads</div>
            <div className="text-xs text-emerald-600 font-medium flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
            </div>
          </div>
          <div className="space-y-2">
            {leads.map((l, i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-slate-100 px-3 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1D6AFF] to-[#0B3FA3] text-white text-xs font-semibold flex items-center justify-center shrink-0">
                    {l.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-slate-900 font-medium text-sm truncate">{l.name}</div>
                    <div className="text-slate-500 text-xs truncate">{l.note}</div>
                  </div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider border rounded-full px-2.5 py-0.5 shrink-0 ${pill[l.tone]}`}>{l.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [term, setTerm] = useState("3m"); // 3m | 12m
  const proPrice = term === "12m" ? 179 : 199;
  const elitePrice = term === "12m" ? 279 : 299;
  const proRegular = 299;
  const eliteRegular = 499;
  const proAnnualSavings = (199 - 179) * 12; // $240
  const eliteAnnualSavings = (299 - 279) * 12; // $240
  const proPlanId = term === "12m" ? "pro_12m" : "pro_3m";
  const elitePlanId = term === "12m" ? "elite_12m" : "elite_3m";

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="landing-page">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Logo size="md" />
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#how" className="hover:text-slate-900 transition-colors">How it works</a>
            <a href="#features" className="hover:text-slate-900 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-slate-900 transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="nav-login"
              onClick={() => navigate("/login")}
              className="hidden sm:inline-flex text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
              Log in
            </button>
            <button
              data-testid="nav-signup"
              onClick={() => navigate("/signup")}
              className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium transition-all"
            >
              Sign up
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-28 pb-24 sm:pt-44 sm:pb-44 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden
        />
        {/* Light overlay — fades image into white at bottom */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/55 to-slate-50" aria-hidden />
        <div className="absolute inset-0 bg-grid mask-radial-fade opacity-30" aria-hidden />

        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 border border-slate-200 text-xs text-slate-600 mb-7 animate-fade-up shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-[#1D6AFF]" />
            The Growth System Built for Real Estate Agents
          </div>
          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] mb-6 animate-fade-up text-slate-900"
            style={{ animationDelay: "0.08s" }}
          >
            Smarter Leads.
            <br />
            <span className="bg-gradient-to-r from-slate-900 via-slate-900 to-[#1D6AFF] bg-clip-text text-transparent">
              Stronger Closings.
            </span>
          </h1>
          <p
            className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-up"
            style={{ animationDelay: "0.16s" }}
          >
            The all-in-one platform built for real estate agents to attract, engage, and close more clients, on autopilot.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 animate-fade-up" style={{ animationDelay: "0.24s" }}>
            <button
              data-testid="hero-cta-primary"
              onClick={() => navigate("/signup")}
              className="group inline-flex items-center justify-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-7 py-3.5 transition-all shadow-[0_18px_40px_-10px_rgba(29,106,255,0.5)] hover:shadow-[0_22px_50px_-10px_rgba(29,106,255,0.6)] hover:-translate-y-0.5"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => document.getElementById("how")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-900 font-semibold rounded-xl px-7 py-3.5 border border-slate-200 transition-all shadow-sm"
            >
              <PlayCircle className="w-4 h-4" />
              See How It Works
            </button>
          </div>
        </div>
      </section>

      <DashboardPreview />

      {/* HOW IT WORKS */}
      <section id="how" className="relative py-14 sm:py-36">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-8 sm:mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#1D6AFF] mb-3">How it works</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              Four steps from <span className="text-[#1D6AFF]">stranger to closing</span>.
            </h2>
            <p className="text-slate-500 text-base sm:text-lg mt-4">From launching ads to closing the deal, Newground runs the lead flow, scoring, creatives and conversations in one place.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {steps.map((s, i) => (
              <div
                key={s.num}
                className="relative bg-white border border-slate-200 rounded-2xl p-7 hover:border-[#1D6AFF]/40 hover:shadow-[0_20px_40px_-15px_rgba(29,106,255,0.18)] hover:-translate-y-1 transition-all duration-300 shadow-sm"
                data-testid={`step-${i + 1}`}
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/20 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-[#1D6AFF]" />
                  </div>
                  <div className="font-display text-3xl font-bold text-slate-200">{s.num}</div>
                </div>
                <h3 className="font-display text-xl font-semibold mb-2 text-slate-900">{s.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="relative py-14 sm:py-36 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-8 sm:mb-14">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#1D6AFF] mb-3">Features</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
              Everything you need to <br className="hidden sm:block" />
              <span className="text-[#1D6AFF]">dominate your market</span>.
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 lg:gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="group bg-slate-50 border border-slate-200 rounded-2xl p-4 sm:p-7 hover:bg-white hover:border-[#1D6AFF]/30 hover:shadow-[0_20px_40px_-15px_rgba(29,106,255,0.18)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/20 flex items-center justify-center mb-3 sm:mb-5 group-hover:bg-[#1D6AFF]/15 transition-colors">
                  <f.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#1D6AFF]" />
                </div>
                <h3 className="font-display text-sm sm:text-xl font-semibold mb-1.5 sm:mb-2 text-slate-900 leading-snug">{f.title}</h3>
                <p className="text-slate-500 text-xs sm:text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <ROICalculator />

      {/* PRICING */}
      <section id="pricing" className="relative py-14 sm:py-36 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#1D6AFF] mb-3">Pricing</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-4 text-slate-900">
              Simple pricing. <span className="text-[#1D6AFF]">Serious results.</span>
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">No contracts. Cancel anytime. Onboarding takes less than 10 minutes.</p>
          </div>

          {/* Launch promo banner */}
          <div className="max-w-4xl mx-auto mb-6 sm:mb-10 flex items-center gap-3 bg-gradient-to-r from-[#1D6AFF]/8 to-amber-50 border border-[#1D6AFF]/25 rounded-2xl px-5 py-3.5" data-testid="launch-promo-banner">
            <div className="w-9 h-9 rounded-xl bg-white border border-[#1D6AFF]/30 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-[#1D6AFF]" />
            </div>
            <p className="text-sm text-slate-700 leading-snug">
              <span className="font-bold text-slate-900">Launch Promo:</span> you're early. Lock in this rate now and it's <span className="font-semibold text-[#1D6AFF]">guaranteed for the lifetime of your subscription</span>.
            </p>
          </div>

          {/* Plan term toggle (3-month vs 12-month) */}
          <div className="flex items-center justify-center mb-6 sm:mb-10" data-testid="term-toggle">
            <div className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm">
              <button
                data-testid="term-3m"
                onClick={() => setTerm("3m")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  term === "3m" ? "bg-[#1D6AFF] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                3-month plan
              </button>
              <button
                data-testid="term-12m"
                onClick={() => setTerm("12m")}
                className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  term === "12m" ? "bg-[#1D6AFF] text-white shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                12-month plan
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${term === "12m" ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700 border border-emerald-200"}`}>
                  Save $240/yr
                </span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* PRO */}
            <div data-testid="pricing-pro-card" className="relative rounded-3xl p-8 sm:p-10 bg-white border-2 border-[#1D6AFF]/50 glow-blue">
              <div className="absolute -top-3 left-8 px-3 py-1 bg-[#1D6AFF] text-xs font-bold uppercase tracking-wider rounded-full text-white flex items-center gap-1.5 shadow-md">
                <Star className="w-3 h-3 fill-white" /> Most Popular
              </div>
              <div className="font-display text-2xl font-semibold mb-1 text-slate-900">Pro</div>
              <div className="text-slate-500 text-sm mb-6">For agents ready to scale buyer leads.</div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-display text-6xl font-bold text-slate-900">${proPrice}</span>
                <span className="text-slate-500">/month</span>
                <span className="text-xl text-slate-400 line-through decoration-2 ml-1">${proRegular}</span>
              </div>
              <div className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 mb-3">
                {term === "12m" ? `Save $${proAnnualSavings}/yr vs 3-month · Your price never goes up` : "Save $100/mo · Your price never goes up"}
              </div>
              <div className="text-xs text-slate-500 mb-7">Billed monthly · {term === "12m" ? "12-month plan" : "3-month plan"}</div>
              <button
                data-testid="pricing-pro-cta"
                onClick={() => navigate(`/signup?plan=${proPlanId}`)}
                className="w-full bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl py-3.5 transition-all shadow-[0_14px_30px_-8px_rgba(29,106,255,0.4)] mb-7"
              >
                Get Started
              </button>
              <ul className="space-y-3">
                {proIncludes.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#1D6AFF] mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* ELITE */}
            <div data-testid="pricing-elite-card" className="relative rounded-3xl p-8 sm:p-10 bg-white border-2 border-[#1D6AFF]/50 glow-blue">
              <div className="absolute -top-3 left-8 px-3 py-1 bg-gradient-to-r from-amber-400 to-amber-600 text-xs font-bold uppercase tracking-wider rounded-full text-white flex items-center gap-1.5 shadow-md">
                <Crown className="w-3 h-3" /> Best Value
              </div>
              <div className="font-display text-2xl font-semibold mb-1 text-slate-900">Elite</div>
              <div className="text-slate-500 text-sm mb-6">For top producers who want the unfair advantage.</div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-display text-6xl font-bold text-slate-900">${elitePrice}</span>
                <span className="text-slate-500">/month</span>
                <span className="text-xl text-slate-400 line-through decoration-2 ml-1">${eliteRegular}</span>
              </div>
              <div className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 mb-3">
                {term === "12m" ? `Save $${eliteAnnualSavings}/yr vs 3-month · Your price never goes up` : "Save $200/mo · Your price never goes up"}
              </div>
              <div className="text-xs text-slate-500 mb-7">Billed monthly · {term === "12m" ? "12-month plan" : "3-month plan"}</div>
              <button
                data-testid="pricing-elite-cta"
                onClick={() => navigate(`/signup?plan=${elitePlanId}`)}
                className="w-full bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl py-3.5 transition-all shadow-[0_14px_30px_-8px_rgba(29,106,255,0.4)] mb-7"
              >
                Get Started
              </button>
              <ul className="space-y-3">
                {eliteIncludes.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Ad spend clarification notice */}
          <div className="max-w-3xl mx-auto mt-10 flex items-start gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-4 shadow-sm" data-testid="ad-spend-notice">
            <div className="w-9 h-9 rounded-xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/25 flex items-center justify-center shrink-0 mt-0.5">
              <Info className="w-4 h-4 text-[#1D6AFF]" />
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-900">All Meta Ads spend is billed directly by Facebook</span>. Newground never touches your ad budget. Your subscription delivers AI-driven ad optimization and automation that converts leads into closing opportunities.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row items-center md:items-start justify-between gap-8">
          <div className="max-w-sm">
            <Logo size="md" />
            <p className="text-sm text-slate-500 mt-4 leading-relaxed">
              The AI growth system for real estate agents. Built to attract, engage, and close.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500">
            <Link to="/privacy" className="hover:text-slate-900 transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-slate-900 transition-colors" data-testid="footer-terms">Terms of Service</Link>
            <a href="mailto:james@newgroundai.com" className="hover:text-slate-900 transition-colors" data-testid="footer-contact">Contact</a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-8 pt-6 border-t border-slate-200 text-xs text-slate-500">
          © 2025 Newground. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
